import React, { useEffect, useRef } from 'react';
import { Card, CardHeader, CardTitle, FormCardContent } from '@/components/ui/card';
import { useLanguage } from '@/contexts/LanguageContext';
import { useInterestDataSync } from '@/hooks/useInterestData';
// Removed useLeadPushing import - FormSubmissionRenderer is display-only
import { useContactExtraction } from '@/hooks/useContactExtraction';
import { logger } from '@/utils/logger';

export interface FormSubmissionData {
  [key: string]: string;
  formType?: string;
}

interface FormSubmissionRendererProps {
  formData: FormSubmissionData;
  formType?: string;
}

const FormSubmissionRenderer: React.FC<FormSubmissionRendererProps> = ({ formData, formType }) => {
  const { t } = useLanguage();
  const storedInterest = useInterestDataSync();
  
  // Use ref to track if lead has been processed to prevent duplicates
  const leadProcessedRef = useRef(false);
  
  // Removed useLeadPushing since FormSubmissionRenderer is display-only
  const { extractContactData, hasValidContactData } = useContactExtraction();

  // Process the lead when component renders (only once)
  useEffect(() => {
    // Prevent duplicate processing
    if (leadProcessedRef.current) {
      return;
    }
    
    const processLead = async () => {
      if (!formData || typeof formData !== 'object') {
        return;
      }

      // Mark as processing to prevent duplicates
      leadProcessedRef.current = true;

      // REMOVED: Lead pushing from FormSubmissionRenderer
      // The FormSubmissionRenderer is only for DISPLAYING form submissions from AI responses
      // Lead pushing is handled in useFormSubmission.ts when forms are actually submitted
    };

    processLead();
  }, [formData, formType]); // Removed function dependencies that were causing re-renders

  // Early return if formData is undefined or null
  if (!formData || typeof formData !== 'object') {
    console.error('FormSubmissionRenderer: formData is invalid:', formData);
    return null;
  }

  // Extract formType from formData if not provided as prop
  const actualFormType = formType || formData.formType;

  // Get stored interest data for vehicle information (already available from hook above)

  const formatFieldLabel = (key: string): string => {
    const labelMap: { [key: string]: string } = {
      name: t('form.name'),
      phone: t('form.phone'),
      email: t('form.email'),
      dateOfBirth: t('form.dateOfBirth'),
      annualIncome: t('form.annualIncome'),
      address: t('form.address'),
      appointmentDate: t('form.appointmentDate'),
      appointmentTime: t('form.appointmentTime'),
      serviceDate: t('form.serviceDate'),
      serviceTime: t('form.serviceTime'),
      interest: t('form.interest'),
      // Vehicle fields
      vehicleMake: t('vehicle.make'),
      vehicleModel: t('vehicle.model'),
      vehicleTrim: t('vehicle.trim'),
      vehicleYear: t('vehicle.year'),
      vehicleVin: 'VIN',
      vehicleStock: t('vehicle.stock'),
      vehiclePrice: t('vehicle.price'),
      vehicleMileage: t('vehicle.mileage'),
      vehicleColor: t('vehicle.color'),
      vehicleExteriorColor: 'Exterior Color',
      vehicleInteriorColor: 'Interior Color',
      vehicleFuelType: 'Fuel Type',
      vehicleEngine: 'Engine',
      vehicleTransmission: 'Transmission'
    };
    return labelMap[key] || key;
  };

  const formatPhoneNumber = (phone: string): string => {
    const cleaned = phone.replace(/\D/g, '');
    
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    
    return phone;
  };

  const formatFieldValue = (key: string, value: string): string => {
    if (key === 'phone' && value) {
      return formatPhoneNumber(value);
    }
    if (key === 'annualIncome' && value) {
      const numericValue = parseInt(value);
      return new Intl.NumberFormat('fr-CA', {
        style: 'currency',
        currency: 'CAD',
        minimumFractionDigits: 0
      }).format(numericValue);
    }
    if (key === 'vehiclePrice' && value) {
      const numericValue = parseInt(value);
      return new Intl.NumberFormat('fr-CA', {
        style: 'currency',
        currency: 'CAD',
        minimumFractionDigits: 0
      }).format(numericValue);
    }
    if (key === 'vehicleMileage' && value) {
      const numericValue = parseInt(value);
      return new Intl.NumberFormat('fr-CA').format(numericValue) + ' km';
    }
    return value;
  };

  // Filter out 'message' field, 'formType' field, and empty values - now safe because we checked formData above
  const availableFields = Object.keys(formData).filter(key => 
    key !== 'message' && key !== 'formType' && formData[key] && formData[key].trim().length > 0
  );

  // Add vehicle fields from stored interest data if available
  const vehicleFields: { [key: string]: string } = {};
  if (storedInterest?.vehicle) {
    if (storedInterest.vehicle.make) vehicleFields.vehicleMake = storedInterest.vehicle.make;
    if (storedInterest.vehicle.model) vehicleFields.vehicleModel = storedInterest.vehicle.model;
    if (storedInterest.vehicle.version) vehicleFields.vehicleTrim = storedInterest.vehicle.version;
    if (storedInterest.vehicle.trim && !storedInterest.vehicle.version) vehicleFields.vehicleTrim = storedInterest.vehicle.trim;
    if (storedInterest.vehicle.year) vehicleFields.vehicleYear = String(storedInterest.vehicle.year);
    if (storedInterest.vehicle.vin) vehicleFields.vehicleVin = storedInterest.vehicle.vin;
    if (storedInterest.vehicle.stock) vehicleFields.vehicleStock = storedInterest.vehicle.stock;
    if (storedInterest.vehicle.price) vehicleFields.vehiclePrice = String(storedInterest.vehicle.price);
    if (storedInterest.vehicle.mileage) vehicleFields.vehicleMileage = String(storedInterest.vehicle.mileage);
    if (storedInterest.vehicle.color) vehicleFields.vehicleColor = storedInterest.vehicle.color;
    if (storedInterest.vehicle.exterior_color) vehicleFields.vehicleExteriorColor = storedInterest.vehicle.exterior_color;
    if (storedInterest.vehicle.interior_color) vehicleFields.vehicleInteriorColor = storedInterest.vehicle.interior_color;
    if (storedInterest.vehicle.fuel_type) vehicleFields.vehicleFuelType = storedInterest.vehicle.fuel_type;
    if (storedInterest.vehicle.engine) vehicleFields.vehicleEngine = storedInterest.vehicle.engine;
    if (storedInterest.vehicle.transmission) vehicleFields.vehicleTransmission = storedInterest.vehicle.transmission;
  }

  // For now, we'll show Stock from the vehicle data if it exists (this might come from vehicle cards)
  // This could be enhanced later to store stock number in interest data
  
  // Define field orders for different form types
  const getFieldOrder = (type?: string) => {
    const baseOrder = ['name', 'phone', 'email'];
    const vehicleOrder = ['vehicleMake', 'vehicleModel', 'vehicleTrim', 'vehicleYear', 'vehiclePrice', 'vehicleMileage', 'vehicleColor', 'vehicleExteriorColor', 'vehicleInteriorColor', 'vehicleFuelType', 'vehicleEngine', 'vehicleTransmission', 'vehicleVin', 'vehicleStock'];
    
    switch (type) {
      case 'appointment':
        return [...baseOrder, 'appointmentDate', 'appointmentTime', ...vehicleOrder, 'interest'];
      case 'service':
        return [...baseOrder, 'serviceDate', 'serviceTime', ...vehicleOrder, 'interest'];
      case 'finance':
      case 'FinanceV1':
        return [...baseOrder, 'dateOfBirth', 'annualIncome', 'address', ...vehicleOrder, 'interest'];
      case 'offer':
      case 'carfax':
        return [...baseOrder, ...vehicleOrder, 'interest'];
      case 'contact':
      default:
        return [...baseOrder, ...vehicleOrder, 'interest'];
    }
  };
  
  const fieldOrder = getFieldOrder(actualFormType);
  
  // Combine form data with vehicle fields
  const allFields = { ...formData, ...vehicleFields };
  const fieldsToShow = fieldOrder.filter(field => 
    (field in allFields && allFields[field] && String(allFields[field]).trim().length > 0) ||
    availableFields.includes(field)
  );

  const getFormTitle = () => {
    switch (actualFormType) {
      case 'appointment':
        return t('form.appointment.submitted');
      case 'service':
        return t('form.service.submitted');
      case 'finance':
      case 'FinanceV1':
        return t('form.finance.submitted');
      case 'offer':
        return t('form.offer.submitted');
      case 'carfax':
        return t('form.carfax.submitted');
      case 'trade':
        return t('form.trade.submitted');
      case 'contact':
      default:
        return t('form.contact.submitted');
    }
  };

  return (
     <Card className="my-3 d2cbot-bot_border d2cbot-bot_border-gray-200 shadow-lg bg-gradient-to-br from-white to-gray-50">
       <CardHeader className="bg-gradient-to-r from-gray-900 to-gray-800 text-white px-4 py-3 d2cbot-bot_border-b d2cbot-bot_border-gray-200 h-auto">
        <CardTitle className="text-sm font-semibold text-white flex items-center gap-2">
          <div className="w-2 h-2 bg-green-400 rounded-full"></div>
          {getFormTitle()}
        </CardTitle>
      </CardHeader>
      <FormCardContent className="p-0">
        <div className="divide-y divide-gray-100">
          {fieldsToShow.map((field) => {
            const value = allFields[field] || formData[field];
            return (
              <div key={field} className="px-4 py-3 hover:bg-gray-50 transition-colors">
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {formatFieldLabel(field)}
                  </span>
                  <span className="text-sm font-medium text-gray-900 break-words">
                    {formatFieldValue(field, value)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </FormCardContent>
    </Card>
  );
};

export default FormSubmissionRenderer;
